import React, { useState, useEffect } from 'react';
import { bijouxAPI } from '../../services/api';
import { 
  Search, MapPin, History, Package, TrendingUp, 
  ArrowRight, Calendar, User, Store, ShoppingBag, 
  AlertCircle, CheckCircle, Clock, Filter, X 
} from 'lucide-react';

function Tracabilite() {
  const [bijoux, setBijoux] = useState([]);
  const [selectedBijou, setSelectedBijou] = useState(null);
  const [traceData, setTraceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');

  const categories = ['bague', 'collier', 'bracelet', 'boucles_oreilles', 'pendentif'];

  useEffect(() => {
    loadBijoux();
  }, []);

  const loadBijoux = async () => {
    try {
      // §113c - limit=2000 ; PAS de stock_only : la traçabilité couvre aussi les pièces vendues
      const response = await bijouxAPI.getAll({ limit: 2000 });
      setBijoux(response.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const loadTrace = async (bijouId) => {
    setLoading(true);
    try {
      const response = await bijouxAPI.getTraceComplete(bijouId);
      setTraceData(response.data);
      setSelectedBijou(response.data.bijou);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement de la traçabilité');
    } finally {
      setLoading(false);
    }
  };

  const filteredBijoux = bijoux.filter(bijou => {
    const term = searchTerm.toLowerCase();
    const matchSearch = !term ||
      (bijou.nom || '').toLowerCase().includes(term) ||
      (bijou.reference || '').toLowerCase().includes(term) ||
      (bijou.reference_article || '').toLowerCase().includes(term) ||
      (bijou.id_produit || '').toLowerCase().includes(term) ||
      (bijou.collection_nom || '').toLowerCase().includes(term) ||
      (bijou.description || '').toLowerCase().includes(term);
    const matchCategorie = !filterCategorie || bijou.categorie === filterCategorie;
    return matchSearch && matchCategorie;
  });

  const getMouvementIcon = (type) => {
    switch(type) {
      case 'entree': return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'sortie': return <ArrowRight className="h-5 w-5 text-red-600" />;
      case 'vente': return <ShoppingBag className="h-5 w-5 text-purple-600" />;
      case 'transfert_sortie': return <ArrowRight className="h-5 w-5 text-blue-600" />;
      case 'transfert_entree': return <TrendingUp className="h-5 w-5 text-blue-600" />;
      case 'ajustement': return <Package className="h-5 w-5 text-orange-600" />;
      default: return <History className="h-5 w-5 text-gray-600" />;
    }
  };

  const getMouvementLabel = (type) => {
    const labels = {
      'entree': 'Entrée en stock',
      'sortie': 'Sortie de stock',
      'vente': 'Vente',
      'transfert_sortie': 'Transfert sortant',
      'transfert_entree': 'Transfert entrant',
      'ajustement': 'Ajustement'
    };
    return labels[type] || type;
  };

  const getTransfertStatusBadge = (statut) => {
    const badges = {
      'en_attente': 'bg-yellow-100 text-yellow-800',
      'valide': 'bg-blue-100 text-blue-800',
      'en_transit': 'bg-purple-100 text-purple-800',
      'recu': 'bg-green-100 text-green-800'
    };
    return badges[statut] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-800 flex items-center space-x-3">
          <History className="h-8 w-8 text-blue-600" />
          <span>Traçabilité des Bijoux</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des bijoux */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
            <Search className="h-5 w-5 text-blue-600" />
            <span>Rechercher un bijou</span>
          </h3>

          {/* Filtres */}
          <div className="space-y-3 mb-4 overflow-x-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Nom ou référence..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={filterCategorie}
                onChange={(e) => setFilterCategorie(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Toutes catégories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {(searchTerm || filterCategorie) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterCategorie('');
                }}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <X className="h-4 w-4" />
                <span>Réinitialiser</span>
              </button>
            )}
          </div>

          {/* Liste */}
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredBijoux.map(bijou => (
              <button
                key={bijou.id}
                onClick={() => loadTrace(bijou.id)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedBijou?.id === bijou.id
                    ? 'bg-blue-50 border-blue-500 shadow-md'
                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  {bijou.reference_article && (
                    <span className="font-mono font-bold text-xs text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">
                      {bijou.reference_article}
                    </span>
                  )}
                  {bijou.id_produit && (
                    <span className="font-mono text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                      {bijou.id_produit}
                    </span>
                  )}
                  {bijou.couleur_or && (
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                      bijou.couleur_or === 'BLANC' ? 'bg-gray-100 text-gray-600' :
                      bijou.couleur_or === 'ROSE'  ? 'bg-pink-100 text-pink-600' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>Or {bijou.couleur_or.toLowerCase()}</span>
                  )}
                </div>
                <div className="font-medium text-sm text-gray-800 mt-1">
                  {bijou.nom && bijou.nom.toUpperCase() !== (bijou.reference_article || '').toUpperCase()
                    ? bijou.nom
                    : bijou.description || bijou.nom || '—'}
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded capitalize">
                      {bijou.categorie}
                    </span>
                    {bijou.collection_nom && (
                      <span className="text-xs text-indigo-600 font-medium">{bijou.collection_nom}</span>
                    )}
                  </div>
                  {(bijou.poids_metal || bijou.poids_brut) && (
                    <span className="text-xs text-amber-700">{bijou.poids_metal || bijou.poids_brut}g</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Détails de la traçabilité */}
        <div className="lg:col-span-2 space-y-6">
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement de la traçabilité...</p>
            </div>
          ) : traceData ? (
            <>
              {/* Informations du bijou */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-md p-6 text-white">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      {traceData.bijou.reference_article && (
                        <span className="font-mono font-bold text-lg bg-white/20 px-2 py-0.5 rounded">
                          {traceData.bijou.reference_article}
                        </span>
                      )}
                      {traceData.bijou.id_produit && (
                        <span className="font-mono text-sm bg-white/15 px-2 py-0.5 rounded">
                          {traceData.bijou.id_produit}
                        </span>
                      )}
                      {traceData.bijou.couleur_or && (
                        <span className="text-xs bg-white/15 px-2 py-0.5 rounded">
                          Or {traceData.bijou.couleur_or.toLowerCase()} 18k
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold">
                      {traceData.bijou.nom && traceData.bijou.nom.toUpperCase() !== (traceData.bijou.reference_article || '').toUpperCase()
                        ? traceData.bijou.nom
                        : traceData.bijou.description || traceData.bijou.nom}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-blue-100">
                      <span className="capitalize">{traceData.bijou.categorie}</span>
                      {traceData.bijou.collection_nom && (
                        <>
                          <span>·</span>
                          <span>{traceData.bijou.collection_nom}</span>
                        </>
                      )}
                      {(traceData.bijou.poids_metal || traceData.bijou.poids_brut) && (
                        <>
                          <span>·</span>
                          <span>{traceData.bijou.poids_metal || traceData.bijou.poids_brut}g</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{traceData.stock_total}</div>
                    <div className="text-blue-100">en stock</div>
                  </div>
                </div>
              </div>

              {/* Localisation actuelle */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                  <MapPin className="h-6 w-6 text-green-600" />
                  <span>Localisation actuelle</span>
                </h3>
                {traceData.localisations.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {traceData.localisations.map((loc, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Store className="h-5 w-5 text-blue-600" />
                            <span className="font-semibold text-gray-800">{loc.boutique_nom}</span>
                          </div>
                          <span className="text-2xl font-bold text-blue-600">{loc.quantite}</span>
                        </div>
                        <div className="text-sm text-gray-600 capitalize">{loc.boutique_type}</div>
                        {loc.adresse && (
                          <div className="text-sm text-gray-500 mt-1">{loc.adresse}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p>Aucun stock disponible</p>
                  </div>
                )}
              </div>

              {/* Transferts en cours */}
              {traceData.transferts_en_cours && traceData.transferts_en_cours.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                    <Clock className="h-6 w-6 text-yellow-600" />
                    <span>Transferts en cours</span>
                  </h3>
                  <div className="space-y-3">
                    {traceData.transferts_en_cours.map((transfert, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <ArrowRight className="h-5 w-5 text-blue-600" />
                          <div>
                            <div className="font-semibold text-gray-800">
                              {transfert.source_nom} → {transfert.destination_nom}
                            </div>
                            <div className="text-sm text-gray-600">Quantité: {transfert.quantite}</div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTransfertStatusBadge(transfert.statut)}`}>
                          {transfert.statut}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Historique des mouvements */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                  <History className="h-6 w-6 text-purple-600" />
                  <span>Historique des mouvements</span>
                </h3>
                {traceData.mouvements.length > 0 ? (
                  <div className="space-y-3">
                    {traceData.mouvements.map((mouvement, index) => (
                      <div key={index} className="border-l-4 border-blue-500 bg-gray-50 rounded-r-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <div className="mt-1">
                              {getMouvementIcon(mouvement.type_mouvement)}
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-800">
                                {getMouvementLabel(mouvement.type_mouvement)}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                <div className="flex items-center space-x-2">
                                  <Store className="h-4 w-4" />
                                  <span>{mouvement.boutique_nom}</span>
                                  <span className="text-gray-400">•</span>
                                  <span className="capitalize">{mouvement.boutique_type}</span>
                                </div>
                              </div>
                              {mouvement.client_nom && (
                                <div className="text-sm text-gray-600 mt-1 flex items-center space-x-2">
                                  <User className="h-4 w-4" />
                                  <span>Client: {mouvement.client_nom}</span>
                                  {mouvement.prix_vente && (
                                    <>
                                      <span className="text-gray-400">•</span>
                                      <span className="font-semibold text-green-600">
                                        {parseFloat(mouvement.prix_vente).toLocaleString()} DH
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}
                              {mouvement.commentaire && (
                                <div className="text-sm text-gray-500 mt-1 italic">
                                  {mouvement.commentaire}
                                </div>
                              )}
                              {mouvement.utilisateur_nom && (
                                <div className="text-xs text-gray-500 mt-2 flex items-center space-x-1">
                                  <User className="h-3 w-3" />
                                  <span>Par: {mouvement.utilisateur_nom}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-2 shrink-0">
                            <div className={`text-lg font-bold ${
                              mouvement.quantite > 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {mouvement.quantite > 0 ? '+' : ''}{mouvement.quantite}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center space-x-1 mt-1">
                              <Calendar className="h-3 w-3" />
                              <span>{new Date(mouvement.date_mouvement).toLocaleDateString('fr-FR')}</span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(mouvement.date_mouvement).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <History className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p>Aucun mouvement enregistré</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <Search className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Sélectionnez un bijou
              </h3>
              <p className="text-gray-600">
                Choisissez un bijou dans la liste pour voir sa traçabilité complète
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Tracabilite;
